import { EmptyView, ErrorView, ListControls, LoadingView, StatusBadge, DashStyles as Styles, TableWrap } from '../../components/dashboard/DashboardUI';
import { usePaginatedList } from '../../components/dashboard/usePaginatedList';
import { Constants } from '../../internal/Constants';
import { Localized } from '../../internal/Localized';
import { useDashboard } from '../../providers/DashboardProvider';
import { Label } from '@app/engine';
import { FormatTimestamp, WrapLTR } from '@shared/common';
import { TextsClient } from '@shared/frontend';
import { useNavigate } from 'react-router-dom';

/**
 * Texts list screen: a server-paginated, searchable list of the tenant's AI-texting conversations, newest activity first.
 *
 * @returns The texts list element.
 */
export function Texts() {
  const { tenantID } = useDashboard();
  const navigate = useNavigate();
  const list = usePaginatedList(
    (signal, offset, limit, pattern) => TextsClient.ListTexts(signal, tenantID!, offset, limit, pattern),
    (signal, pattern) => TextsClient.CountTexts(signal, tenantID!, pattern),
    [tenantID]
  );

  return (
    <>
      <ListControls
        searchValue={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder={Localized.TextsSearchPlaceholder}
        offset={list.offset}
        limit={list.limit}
        total={list.total}
        pageCount={list.data.length}
        onOffsetChange={list.setOffset}
        onLimitChange={list.setLimit}
        loading={list.loading}
      />
      {list.loading ? (
        <LoadingView />
      ) : list.error ? (
        <ErrorView onRetry={list.reload} />
      ) : list.data.length === 0 ? (
        <EmptyView message={Localized.TextsNone} />
      ) : (
        <TableWrap>
          <table className={Styles.table}>
            <thead>
              <tr>
                <th>
                  <Label text={Localized.TextsCustomer} />
                </th>
                <th>
                  <Label text={Localized.SharedStatus} />
                </th>
                <th className={Styles.cellFill}>
                  <Label text={Localized.TextsLastMessage} />
                </th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((text) => (
                <tr key={text.id} className={Styles.rowClickable} onClick={() => navigate(Constants.Route.TextDetail.replace(`:${Constants.Param.TextID}`, text.id))}>
                  <td className={Styles.cellNoWrap}>
                    {/* Customer number with the last-activity time stacked beneath it - one compact identity cell instead of a separate When column (mirrors the calls list). */}
                    <div className={Styles.cellStack}>
                      <Label text={WrapLTR(text.customerPhoneE164)} />
                      <Label classNameContainer={Styles.muted} text={FormatTimestamp(text.lastMessageAt)} />
                    </div>
                  </td>
                  <td>
                    <div className={Styles.cellStack}>
                      <StatusBadge status={text.status} />
                      {/* An opted-out customer (replied STOP) is the one state the owner must never miss, so it rides alongside the lifecycle badge. */}
                      {text.optedOutAt ? <Label classNameContainer={Styles.muted} text={Localized.TextsOptedOut} /> : null}
                    </div>
                  </td>
                  <td>
                    <Label classNameContainer={Styles.muted} text={text.lastMessagePreview ?? '—'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </>
  );
}
